import {
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import RecipientsSection from "../../components/RecipientSection/RecipientsSection";
import DesignEmail from "../../components/DesignEmail";
import { useContext, useEffect, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { cloneDeep } from "lodash";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import { CampaignContext } from "../../Context";
import SplitRulesSection from "./SplitRulesSection";
import FromSectionTestingVariable from "./FromSectionTestingVariable";
import SubjectSectionTestingVariable from "./SubjectSectionTestingVariable";
import DesignSectionTestingVariable from "./DesignSectionTestingVariable";
import ScheduleSectionTestingVariable from "./ScheduleSectionTestingVariable";
import { deliveryOptions, variables, winningMetric } from "../Constant";
import FromSection from "../../components/FromSection";
import SubjectSection from "../../components/SubjectSection";
import DesignSection from "../../components/DesignSection";
import { submitABCampaign } from "screens/Campaigns/CampaignService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { useAppSelector } from "hooks/use-app-select";
import {
  changeIsCampaignSubmitted,
  selectIsCampaignSubmitted,
} from "redux/generalSlice";
import { useAppDispatch } from "utils/hooks";
import AttachmentSection from "../../components/AttachmentSection";

interface CustomProps {
  setSkipStepOne: Function;
}

const Step2 = (props: CustomProps) => {
  const dispatch = useAppDispatch();
  const isCampaignSubmitted = useAppSelector(selectIsCampaignSubmitted);
  const [isDesignEmailModalOpen, setIsDesignEmailModalOpen] = useState(false);
  const { submitCampaign, setSubmitCampaign, managingStep, saveAsDraft } =
    useContext(CampaignContext);
  const [saving, setSaving] = useState<boolean>(false);
  const [editName, setEditName] = useState(false);
  const [formFields, setFormFields] = useState({
    name: {
      value: "",
      error: "",
    },
  });
  const [design, setDesign] = useState<{
    index: number | null;
    content: string;
    contentJson: any;
    contentPlain: string;
  }>({
    index: null,
    content: submitCampaign.campaign?.content ?? "",
    contentJson: submitCampaign.campaign?.contentJson ?? {},
    contentPlain: submitCampaign.campaign?.contentPlain ?? "",
  });
  const [designAB, setDesignAB] = useState<
    {
      index: number;
      content: string;
      contentJson: any;
      contentPlain: string;
    }[]
  >([]);
  const [submittingCampaign, setSubmittingCampaign] = useState(false);

  useEffect(() => {
    const design = Array.from({
      length: submitCampaign.abTestDetails?.testingVariants ?? 0,
    }).map((_, i) => {
      if (isTruthy(submitCampaign.abTestDetails?.testingValues[i])) {
        return {
          index: i,
          content: submitCampaign.abTestDetails?.testingValues[i] ?? "",
          contentJson: {},
          contentPlain: "",
        };
      }
      return { index: i, content: "", contentJson: {}, contentPlain: "" };
    });
    setDesignAB(design);
  }, []);

  const saveCampaignName = async () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (!isTruthy(clonedFormFields.name.value)) {
      error = true;
      clonedFormFields.name.error = "Please enter the campaign name";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }
    const updatedCampaign = {
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        name: formFields.name.value.trim(),
      },
    };
    setSubmitCampaign(updatedCampaign);
    setSaving(true);
    setEditName(false);
    await saveAsDraft(updatedCampaign);
    setSaving(false);
  };

  const getInformationLine = () => {
    return (
      <Typography variant="body2">
        {`The A/B test will include ${
          submitCampaign.abTestDetails?.testingVariants
        } versions of the ${variables[
          submitCampaign.abTestDetails?.testingVariable!
        ].label.toLocaleLowerCase()}, with the winning version chosen based on the ${
          winningMetric[submitCampaign.abTestDetails?.winningMetric!].short
        }. The test will run for ${
          submitCampaign.abTestDetails?.resultsAfterInterval
        } ${submitCampaign.abTestDetails?.resultsAfterType.toLocaleLowerCase()}, and the winning version will be ${deliveryOptions[
          submitCampaign.abTestDetails?.winningExecutionType!
        ].label.toLocaleLowerCase()}.`}{" "}
        <span
          style={{ textDecoration: "underline", cursor: "pointer" }}
          onClick={() => props.setSkipStepOne(false)}
        >
          Make change
        </span>
        .
      </Typography>
    );
  };

  const runABTest = async () => {
    try {
      setSaving(true);
      const clonedCampaign = cloneDeep(submitCampaign);
      if (clonedCampaign.abTestDetails?.testingVariable === "content") {
        clonedCampaign.abTestDetails.testingValues = designAB.map(
          (item) => item.content
        );
        clonedCampaign.plainTextOfCampaignContent = designAB.map(
          (item) => item.contentPlain
        );
      } else {
        clonedCampaign.plainTextOfCampaignContent = [design.contentPlain];
      }
      const response = await submitABCampaign(clonedCampaign);
      openSuccessNotification(response.message);
      setSaving(false);
      dispatch(changeIsCampaignSubmitted(!isCampaignSubmitted));
      history.push(
        `${
          viewpaths.campaignsViewPath
        }?page=${1}&rowsPerPage=${10}&currentTab=email`
      );
    } catch (error: any) {
      setSaving(false);
      handleErrorMessage(error);
    }
  };

  const disableAB = () => {
    let error = false;
    if (
      !isTruthy(submitCampaign.campaignSegments.segmentIds) ||
      !isTruthy(submitCampaign.campaignSegments.segmentTypeId) ||
      !isTruthy(submitCampaign.abTestDetails?.ratioForTesting) ||
      submitCampaign.abTestDetails?.ratioForTesting === 0 ||
      !isTruthy(submitCampaign.campaignData.domainName) ||
      submitCampaign.abTestDetails?.testingVariants !=
        submitCampaign.abTestDetails?.testingValues.length ||
      submitCampaign.abTestDetails?.testingValues.some(
        (child) => !isTruthy(child)
      )
    ) {
      error = true;
    }
    if (submitCampaign.abTestDetails?.testingVariable === "fromName") {
      error =
        error ||
        !isTruthy(submitCampaign.campaign.subject) ||
        !isTruthy(submitCampaign.campaign.content);
    }
    if (submitCampaign.abTestDetails?.testingVariable === "subjectLine") {
      error =
        error ||
        !isTruthy(submitCampaign.campaign.fromId) ||
        !isTruthy(submitCampaign.campaign.senderEmail) ||
        !isTruthy(submitCampaign.campaign.content);
    }
    if (submitCampaign.abTestDetails?.testingVariable === "content") {
      error =
        error ||
        !isTruthy(submitCampaign.campaign.fromId) ||
        !isTruthy(submitCampaign.campaign.senderEmail) ||
        !isTruthy(submitCampaign.campaign.subject);
    }
    if (submitCampaign.abTestDetails?.testingVariable === "scheduleTime") {
      error =
        error ||
        !isTruthy(submitCampaign.campaign.fromId) ||
        !isTruthy(submitCampaign.campaign.senderEmail) ||
        !isTruthy(submitCampaign.campaign.subject) ||
        !isTruthy(submitCampaign.campaign.content);
    }
    return error;
  };

  const getHeader = () => {
    return (
      <Stack spacing={2}>
        <Stack direction="row" px={2} justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => props.setSkipStepOne(false)}
              disabled={isTruthy(managingStep)}
            >
              <SvgIcon>
                <KeyboardBackspaceIcon fontSize="small" />
              </SvgIcon>
            </IconButton>
            {editName ? (
              <TextField
                size="small"
                variant="outlined"
                placeholder="Campaign Name"
                value={formFields.name.value}
                onChange={(event: any) => {
                  setFormFields({
                    ...formFields,
                    name: {
                      value: event.target.value,
                      error: "",
                    },
                  });
                }}
                required
                error={isTruthy(formFields.name.error)}
                inputProps={{ minLength: 1, maxLength: 90 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ fontSize: "0.75rem" }}>
                        {formFields.name?.value?.length} / 90
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            ) : (
              <Typography
                variant="h4"
                noWrap
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                maxWidth="250px"
                display="block"
              >
                {submitCampaign.campaign.name}
              </Typography>
            )}
            {editName ? (
              <>
                <Tooltip title="Save" arrow>
                  <IconButton
                    onClick={saveCampaignName}
                    disabled={isTruthy(managingStep)}
                  >
                    <SvgIcon>
                      <CheckIcon fontSize="small" />
                    </SvgIcon>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel" arrow>
                  <IconButton
                    onClick={() => setEditName(false)}
                    disabled={isTruthy(managingStep)}
                  >
                    <SvgIcon>
                      <CloseIcon fontSize="small" />
                    </SvgIcon>
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Edit" arrow>
                <IconButton
                  disabled={isTruthy(managingStep)}
                  onClick={() => {
                    setFormFields({
                      ...formFields,
                      name: {
                        error: "",
                        value: submitCampaign.campaign.name,
                      },
                    });
                    setEditName(true);
                  }}
                >
                  <SvgIcon>
                    <ModeEditIcon fontSize="small" />
                  </SvgIcon>
                </IconButton>
              </Tooltip>
            )}
            <Chip label={submitCampaign.campaign.status} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={runABTest}
              disabled={isTruthy(managingStep) || disableAB()}
            >
              Start A/B test
            </Button>
          </Stack>
        </Stack>
        <Stack sx={{ backgroundColor: "#DCE2EB", borderRadius: "8px" }} p={2}>
          {getInformationLine()}
        </Stack>
      </Stack>
    );
  };

  const getBody = () => {
    const testingVariable = submitCampaign.abTestDetails?.testingVariable;
    return (
      <Card>
        <Stack spacing={4} py={4} divider={<Divider />}>
          {testingVariable === "fromName" ? (
            <FromSectionTestingVariable />
          ) : (
            <FromSection />
          )}
          <RecipientsSection />
          <SplitRulesSection />
          {testingVariable === "subjectLine" ? (
            <SubjectSectionTestingVariable />
          ) : (
            <SubjectSection />
          )}
          {testingVariable === "scheduleTime" && (
            <ScheduleSectionTestingVariable />
          )}
          {testingVariable === "content" ? (
            <DesignSectionTestingVariable
              setIsDesignEmailModalOpen={setIsDesignEmailModalOpen}
              design={design}
              setDesign={setDesign}
              designAB={designAB}
            />
          ) : (
            <DesignSection
              setIsDesignEmailModalOpen={setIsDesignEmailModalOpen}
              design={design}
            />
          )}
          <AttachmentSection />
        </Stack>
      </Card>
    );
  };

  const doneEditing = async (content: any) => {
    let campaignObject: any;
    setSaving(true);
    if (submitCampaign.abTestDetails?.testingVariable === "content") {
      setDesign(content);
      const clonedDesignAB = cloneDeep(designAB);
      clonedDesignAB[design.index!] = {
        index: design.index!,
        content: content.content,
        contentJson: content.contentJson,
        contentPlain: content.contentPlain,
      };
      setDesignAB(clonedDesignAB);
      campaignObject = {
        ...submitCampaign,
        abTestDetails: {
          ...submitCampaign.abTestDetails,
          testingValues: clonedDesignAB.map((internal) => internal.content),
        },
      };
      setSubmitCampaign(campaignObject);
    } else {
      setDesign(content);
      campaignObject = {
        ...submitCampaign,
        campaign: {
          ...submitCampaign.campaign,
          content: content.content,
          contentJson: content.contentJson,
        },
      };
      setSubmitCampaign(campaignObject);
    }
    await saveAsDraft(campaignObject);
    setSaving(false);
    setIsDesignEmailModalOpen(false);
  };

  const getDesignEmailModal = () => {
    return (
      <DesignEmail
        handleDialogClose={() => setIsDesignEmailModalOpen(false)}
        isDialogOpen={isDesignEmailModalOpen}
        doneEditing={doneEditing}
        design={design}
        setDesign={setDesign}
      />
    );
  };

  const getPage = () => {
    return (
      <Stack spacing={4}>
        {getHeader()}
        {getBody()}
      </Stack>
    );
  };

  return (
    <>
      {getPage()}
      {getDesignEmailModal()}
      <CustomLoader isLoading={saving || submittingCampaign} />
    </>
  );
};

export default Step2;
